import React, { useState, useEffect } from 'react';
import { FlatList, Pressable, Platform, useWindowDimensions } from 'react-native';
import { AntDesign, FontAwesome, MaterialIcons } from '@expo/vector-icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { GoogleIcon } from '@assets/--native';
import * as Animatable from 'react-native-animatable';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import Markdown from "react-native-markdown-display";
import WebView from 'react-native-webview';
import ErrorBoundary from '@utils/ErrorBoundary';
import { YStack, XStack, Button, Text, View, Card, Avatar, Separator } from 'tamagui';

type HistoryComponentProps = {
  history: any,
  keyboardCallback?: (val: boolean) => void,
  historyCallback?: (val: any) => void
}

const HistoryComponent = ({ history = { date: new Date(), index: 0, content: {}, mail: 'No mail history found', phone: 'No phone history found', notes: 'No notes history found' }, keyboardCallback, historyCallback }: HistoryComponentProps) => {
  const [itemSelected, setItemSelected] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const { width } = useWindowDimensions();
  const mailAccounts = useSelector((state: DefaultRootState) => state.promiseData.data.userData.authentications.mail);
  const [activeState, setActiveState] = useState('mail');
  const [value, setValue] = useState(activeState == 'mail' ? JSON.stringify(history[activeState]?.snippet) : JSON.stringify(history[activeState]));
  const googleAuth = googleAuths();
  const theme = themeSelector();

  const getTabIcon = (tabName: string) => {
    switch (tabName) {
      case 'mail': return 'mail';
      case 'message': return 'message';
      case 'phone': return 'phone';
      case 'calendar': return 'event';
      case 'notes': return 'note';
      case 'whatsapp': return 'chat';
      default: return 'mail';
    }
  };

  const getTabColor = (tabName: string) => {
    return activeState === tabName 
      ? '#6366f1' // Indigo primary
      : theme 
        ? '#64748b' // Slate grey for light mode
        : '#94a3b8'; // Lighter grey for dark mode
  };

  const getItemCount = (tabName: string) => {
    return Array.isArray(history[tabName]) ? history[tabName].length : 0;
  };

  const body = () => {
    if (itemSelected != -1) {
      if (history[activeState][itemSelected].payload != null) {
        const partMail = history[activeState][itemSelected]?.payload?.parts?.find(parts => parts.mimeType === 'text/html')
        if (partMail != null) {
          let html = Buffer.from(partMail.body.data, 'base64').toString();
          return html
        }
      }
      else {
        let html = Buffer.from(history[activeState][itemSelected].payload.body.data, 'base64').toString();
        return html
      }
    }
  }

  const customHtml = body();

  useEffect(() => {
    _handleValueChange('mail')
  }, [history])

  const _handleValueChange = (val) => {
    setIsLoading(true);
    setActiveState(val);
    setItemSelected(-1);
    setValue(val == 'mail' ? JSON.stringify(history[val]?.snippet) : JSON.stringify(history[val]));
    // Simulate loading delay for smooth transition
    setTimeout(() => setIsLoading(false), 200);
  }

  const handleItemSelect = (item, index) => {
    setValue(JSON.stringify(item));
    setItemSelected(index)
  }

  return (
    <YStack flex={1} backgroundColor={theme ? '$background' : '$backgroundDark'}>
      {/* Header with Google Account Info */}
      {mailAccounts?.mailAuthenticated && (
        <Animatable.View 
          useNativeDriver 
          animation={'slideInUp'} 
          style={{ alignSelf: 'flex-end', paddingHorizontal: 20, paddingTop: 10 }}
        >
          <Card
            size="$3"
            backgroundColor={theme ? '#ffffff' : '#1e293b'}
            borderRadius={16}
            paddingHorizontal={16}
            paddingVertical={8}
            shadowColor="$shadowColor"
            shadowRadius={8}
            shadowOpacity={0.15}
            elevation={4}
          >
            {mailAccounts?.mailType === 'google' && (
              <XStack alignItems="center" gap={12}>
                <GoogleIcon style={{ height: 24, width: 24 }} />
                <Text 
                  numberOfLines={1} 
                  fontSize={14}
                  fontWeight="500"
                  color={theme ? '$color' : '$colorDark'}
                >
                  {googleAuth.email}
                </Text>
              </XStack>
            )}
          </Card>
        </Animatable.View>
      )}

      {/* Main History Container */}
      <Card
        flex={1}
        margin={16}
        marginTop={8}
        backgroundColor={theme ? '#ffffff' : '#1e293b'}
        borderRadius={20}
        shadowColor="$shadowColor"
        shadowRadius={12}
        shadowOpacity={0.1}
        elevation={8}
        overflow="hidden"
      >
        <XStack flex={1}>
          {/* Navigation Sidebar */}
          <YStack 
            width={64}
            backgroundColor={theme ? '#f8fafc' : '#0f172a'}
            borderRightWidth={1}
            borderRightColor={theme ? '#e2e8f0' : '#334155'}
          >
            {['mail', 'message', 'phone', 'calendar', 'notes', 'whatsapp'].map((tab) => {
              const itemCount = getItemCount(tab);
              return (
                <View key={tab} position="relative">
                  <Button
                    flex={1}
                    variant="outlined"
                    backgroundColor={activeState === tab ? (theme ? '#6366f1' : '#4f46e5') : 'transparent'}
                    borderRadius={0}
                    onPress={() => _handleValueChange(tab)}
                    icon={
                      <MaterialIcons
                        name={getTabIcon(tab)}
                        size={24}
                        color={activeState === tab ? '#ffffff' : getTabColor(tab)}
                      />
                    }
                  />
                  {itemCount > 0 && (
                    <View
                      position="absolute"
                      top={8}
                      right={8}
                      backgroundColor="#ef4444"
                      borderRadius={10}
                      minWidth={20}
                      height={20}
                      justifyContent="center"
                      alignItems="center"
                      zIndex={10}
                    >
                      <Text
                        color="#ffffff"
                        fontSize={10}
                        fontWeight="bold"
                      >
                        {itemCount > 99 ? '99+' : itemCount}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })}
          </YStack>
          {/* Content Area */}
          <XStack flex={1}>
            {/* History List */}
            <Animatable.View 
              key={activeState} // Re-animate when activeState changes
              animation="fadeInRight"
              duration={300}
              transition={['flex', 'borderRightWidth']} 
              style={{ 
                flex: itemSelected !== -1 ? 1 : 2,
                borderRightWidth: itemSelected !== -1 ? 1 : 0,
                borderRightColor: theme ? '#e2e8f0' : '#334155'
              }}
            >
              <FlatList 
                style={{ flex: 1, padding: 16 }}
                data={isLoading ? [] : history[activeState]} 
                keyExtractor={(item, index) => index.toString()}
                ListEmptyComponent={() => (
                  <YStack padding={20} alignItems="center">
                    {isLoading ? (
                      <YStack alignItems="center" gap={16}>
                        <MaterialIcons 
                          name="refresh" 
                          size={48} 
                          color={theme ? '#6366f1' : '#4f46e5'}
                        />
                        <Text 
                          textAlign="center" 
                          color={theme ? '$grey10' : '$grey8'}
                          fontSize={16}
                        >
                          Loading {activeState} history...
                        </Text>
                      </YStack>
                    ) : (
                      <YStack alignItems="center" gap={16}>
                        <MaterialIcons 
                          name={getTabIcon(activeState)} 
                          size={48} 
                          color={theme ? '#cbd5e1' : '#475569'} 
                        />
                        <Text 
                          textAlign="center" 
                            color={theme ? '$grey10' : '$grey8'}
                          fontSize={16}
                        >
                          No {activeState} history found
                        </Text>
                        <Text 
                          textAlign="center" 
                            color={theme ? '$grey9' : '$grey7'}
                          fontSize={14}
                        >
                          {activeState === 'mail' 
                            ? 'Connect your email account to see your mail history'
                            : `Your ${activeState} history will appear here when available`
                          }
                        </Text>
                      </YStack>
                    )}
                  </YStack>
                )}
                renderItem={({ item, index }) => (
                  <Card
                    marginBottom={12}
                    backgroundColor={itemSelected === index 
                      ? (theme ? '#f0f9ff' : '#1e3a8a')
                      : (theme ? '#ffffff' : '#334155')
                    }
                    borderColor={itemSelected === index 
                      ? '#6366f1' 
                      : (theme ? '#e2e8f0' : '#475569')
                    }
                    borderWidth={itemSelected === index ? 2 : 1}
                    borderRadius={12}
                    overflow="hidden"
                    pressStyle={{ scale: 0.98 }}
                    onPress={() => handleItemSelect(item, index)}
                  >
                    {activeState === 'mail' ? (
                      <XStack padding={16} gap={12} alignItems="flex-start">
                        {/* Direction Indicator */}
                        <View
                          width={40}
                          height={40}
                          borderRadius={20}
                          backgroundColor={item.labelIds?.includes?.("INBOX") ? '#22c55e' : '#ef4444'}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <MaterialIcons
                            name={item.labelIds?.includes?.("INBOX") ? "inbox" : "send"}
                            size={20}
                            color="#ffffff"
                          />
                        </View>
                        
                        {/* Content */}
                        <YStack flex={1} gap={8}>
                          <View
                            backgroundColor={theme ? '#f8fafc' : '#1e293b'}
                            borderRadius={8}
                            padding={12}
                          >
                            <Markdown
                              style={{
                                body: {
                                  color: theme ? '#1f2937' : '#f9fafb',
                                  fontSize: 14,
                                  lineHeight: 20
                                }
                              }}
                            >
                              {item?.snippet || 'No content'}
                            </Markdown>
                          </View>
                          
                          {itemSelected !== index && (
                            <XStack justifyContent="space-between" alignItems="center">
                              <Text 
                                fontSize={12}
                                color={theme ? '$grey10' : '$grey8'}
                                fontWeight="500"
                              >
                                {moment(item?.date || Number(item?.internalDate)).format("MMM DD, YYYY")}
                              </Text>
                              <Text 
                                fontSize={12}
                                color={theme ? '$grey9' : '$grey7'}
                              >
                                {moment(item?.date || Number(item?.internalDate)).format("h:mm A")}
                              </Text>
                            </XStack>
                          )}
                        </YStack>
                      </XStack>
                    ) : activeState === 'whatsapp' ? (
                      <XStack padding={16} gap={12} alignItems="flex-start">
                        {/* Direction Indicator */}
                        <View
                          width={40}
                          height={40}
                          borderRadius={20}
                          backgroundColor={item.direction === 'inbound' || item.direction === 'incoming' ? '#25D366' : '#128C7E'}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <MaterialIcons
                            name={item.direction === 'inbound' || item.direction === 'incoming' ? "arrow-downward" : "arrow-upward"}
                            size={20}
                            color="#ffffff"
                          />
                        </View>
                        
                        {/* Content */}
                        <YStack flex={1} gap={8}>
                          <View
                            backgroundColor={theme ? '#f8fafc' : '#1e293b'}
                            borderRadius={8}
                            padding={12}
                          >
                            <Text
                              fontSize={14}
                              color={theme ? '#1f2937' : '#f9fafb'}
                              lineHeight={20}
                            >
                              {item.text || item.content?.body || item.content?.text || 'No content'}
                            </Text>
                            {item.mediaUrl && (
                              <Text
                                fontSize={12}
                                color={theme ? '#6b7280' : '#9ca3af'}
                                marginTop={8}
                              >
                                📎 Media: {item.mediaType || 'attachment'}
                              </Text>
                            )}
                          </View>
                          
                          {itemSelected !== index && (
                            <XStack justifyContent="space-between" alignItems="center">
                              <Text 
                                fontSize={12}
                                color={theme ? '$grey10' : '$grey8'}
                                fontWeight="500"
                              >
                                {moment(item?.timestamp || item?.date).format("MMM DD, YYYY")}
                              </Text>
                              <Text 
                                fontSize={12}
                                color={theme ? '$grey9' : '$grey7'}
                              >
                                {moment(item?.timestamp || item?.date).format("h:mm A")}
                              </Text>
                            </XStack>
                          )}
                        </YStack>
                      </XStack>
                    ) : (
                      <View padding={16}>
                        <Text 
                          fontSize={14}
                          color={theme ? '$color' : '$colorDark'}
                        >
                          {JSON.stringify(item)}
                        </Text>
                      </View>
                    )}
                  </Card>
                )}
              />
            </Animatable.View>

            {/* Email Detail View */}
            {itemSelected !== -1 && (
              <YStack flex={1} backgroundColor={theme ? '#fefefe' : '#0f172a'}>
                <XStack 
                  padding={16} 
                  borderBottomWidth={1} 
                  borderBottomColor={theme ? '#e2e8f0' : '#334155'}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text fontSize={16} fontWeight="600" color={theme ? '$color' : '$colorDark'}>
                    Email Details
                  </Text>
                  <Button
                    size="$3"
                    variant="outlined"
                    icon={<MaterialIcons name="close" size={16} />}
                    onPress={() => setItemSelected(-1)}
                  >
                    Close
                  </Button>
                </XStack>
                
                <View style={{ flex: 1, padding: 16 }}>
                  <WebView 
                    originWhitelist={['*']} 
                    source={{ html: customHtml || '<p>No content available</p>' }} 
                    style={{ 
                      flex: 1,
                      borderRadius: 8,
                      backgroundColor: theme ? '#ffffff' : '#1e293b'
                    }}
                    injectedJavaScript={`
                      const viewport = document.querySelector('meta[name="viewport"]');
                      if (viewport) {
                        viewport.setAttribute('content', 'width=${width}, initial-scale=1.0, maximum-scale=1.0');
                      } else {
                        const meta = document.createElement('meta');
                        meta.name = 'viewport';
                        meta.content = 'width=${width}, initial-scale=1.0, maximum-scale=1.0';
                        document.getElementsByTagName('head')[0].appendChild(meta);
                      }
                      document.body.style.backgroundColor = '${theme ? '#ffffff' : '#1e293b'}';
                      document.body.style.color = '${theme ? '#1f2937' : '#f9fafb'}';
                      true;
                    `}
                  />
                </View>
              </YStack>
            )}
          </XStack>
        </XStack>
      </Card>
    </YStack>
  );
};
export default HistoryComponent;